import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Heart, Droplet, Apple, Cross, ChevronRight, Home, MapPin, Clock } from 'lucide-react'
import DecisionPoint from './DecisionPoint'

export default function ChapterScreen({
  pathTitle,
  pathIcon,
  chapter,
  chapterNumber,
  totalChapters,
  decisions = [],
  userProgress,
  onDecisionComplete,
  onChapterComplete
}) {
  const [currentDecision, setCurrentDecision] = useState(0)
  const [completedDecisions, setCompletedDecisions] = useState([])

  const handleChoiceSelect = (choiceIndex, consequence) => {
    // Mark decision as completed
    const newCompleted = [...completedDecisions, currentDecision]
    setCompletedDecisions(newCompleted)

    // Notify parent of decision
    if (onDecisionComplete) {
      onDecisionComplete({
        chapterId: chapter.id,
        decisionId: decisions[currentDecision].id,
        choice: choiceIndex,
        consequence
      })
    }

    // Wait 2 seconds then move to next decision or complete chapter
    setTimeout(() => {
      if (currentDecision + 1 < decisions.length) {
        setCurrentDecision(currentDecision + 1)
      } else {
        // Chapter complete
        if (onChapterComplete) {
          onChapterComplete(chapter.id)
        }
      }
    }, 2000)
  }

  const objectives = chapter.objectives ? JSON.parse(chapter.objectives) : []
  const lessonReferences = chapter.lesson_references ? JSON.parse(chapter.lesson_references) : []

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Path Info */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">{pathIcon || '🎯'}</span>
            <div>
              <h1 className="text-2xl font-bold">{pathTitle}</h1>
              <p className="text-gray-300 text-sm">
                Chapter {chapterNumber} of {totalChapters}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-gray-700 rounded-full h-2 overflow-hidden mb-4">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${((chapterNumber - 1) / totalChapters) * 100}%` }}
              transition={{ duration: 0.5 }}
              className="h-full bg-gradient-to-r from-green-400 to-green-600"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Chapter Title & Metadata */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border-2 border-gray-200 p-8 mb-6"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{chapter.title}</h2>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-4 mb-6 text-sm">
            {chapter.location && (
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4" />
                <span>{chapter.location}</span>
              </div>
            )}
            {chapter.time_offset && (
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-4 h-4" />
                <span>{chapter.time_offset}</span>
              </div>
            )}
          </div>

          {/* Narrative */}
          <div className="prose prose-lg max-w-none mb-6">
            <p className="text-gray-700 leading-relaxed italic border-l-4 border-green-500 pl-4">
              {chapter.narrative}
            </p>
          </div>

          {/* Objectives */}
          {objectives.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Chapter Objectives:</h3>
              <ul className="space-y-1">
                {objectives.map((objective, index) => (
                  <li key={index} className="text-blue-800 flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span>{objective}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>

        {/* Resource Status */}
        {userProgress && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-4 gap-4 mb-8"
          >
            {/* Health */}
            <div className="bg-white rounded-xl border-2 border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-5 h-5 text-red-500" />
                <span className="text-sm font-semibold text-gray-700">Health</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {userProgress.health_status || 100}%
              </div>
              <div className="h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
                <div
                  className="h-full bg-red-500"
                  style={{ width: `${userProgress.health_status || 100}%` }}
                />
              </div>
            </div>

            {/* Resources */}
            {userProgress.resource_inventory && (() => {
              const inventory = JSON.parse(userProgress.resource_inventory)
              const resources = [
                { key: 'water', icon: Droplet, color: 'blue', label: 'Water' },
                { key: 'food', icon: Apple, color: 'green', label: 'Food' },
                { key: 'medical', icon: Cross, color: 'red', label: 'Medical' }
              ]

              return resources.map(({ key, icon: Icon, color, label }) => (
                <div key={key} className="bg-white rounded-xl border-2 border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-5 h-5 text-${color}-500`} />
                    <span className="text-sm font-semibold text-gray-700">{label}</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {inventory[key] || 0}
                  </div>
                </div>
              ))
            })()}
          </motion.div>
        )}

        {/* Decision Points */}
        <AnimatePresence mode="wait">
          {decisions.length > 0 && currentDecision < decisions.length && (
            <motion.div
              key={currentDecision}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <DecisionPoint
                {...decisions[currentDecision]}
                onChoiceSelect={handleChoiceSelect}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Continue Button (shown after all decisions) */}
        {completedDecisions.length === decisions.length && decisions.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-8 text-center"
          >
            <button
              onClick={() => onChapterComplete && onChapterComplete(chapter.id)}
              className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-green-700 transition-colors"
            >
              <span>Continue to Next Chapter</span>
              <ChevronRight className="w-6 h-6" />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  )
}
